import React, { useEffect, useRef, useState } from 'react'
import { effectiveWindow, staleRangeLabel } from './tracpatch.window'
import { connect } from 'react-redux'
import { Row, Button } from 'antd'
import * as d3 from 'd3'
import moment from 'moment'
import '../../../css/patient.scss'
import { CHART_COLORS } from '../../../util/chartPalette'
import { cadenceRangeForDayOffset } from '../../schedule/tracpatch.cadence.defaults'
import { DataViewToggle, ChartDataGrid } from '../../graph/chartDataView'
import { CaretLeftOutlined, CaretRightOutlined } from '@ant-design/icons'
import { quarterHourCadence, quarterRangeLabel, earliestDay, isTodayOrLater, dayBounds, compactHourLabel } from './tracpatch.hourly'
import { styleAxisTicks, AXIS_TITLE_FONT_SIZE, AXIS_TITLE_FONT_WEIGHT, ShowValuesSwitch } from './tracpatch.chart.style'

// Cadence (steps per minute, while walking) over the last 7, 14 or 30 days.
//
// Definition (record-based: each steps record is one walking minute):
//   walkingMinutes = count of records where steps > 0
//   totalSteps     = sum(steps) across walking minutes only
//   maxCadence     = max(steps) across walking minutes -- the day's PEAK
//
// The line plots maxCadence (changed from the mean on 2026-10-14: the peak is
// what the clinician asked to see; the mean sat below it and hid a good walk
// behind a slow day). walkingMinutes / totalSteps stay in the tooltip.
//
// A day with zero walking minutes produces a gap in the line, not a zero —
// rest days shouldn't pull the line to zero and dominate the y-axis.

const TracPatchCadenceChart = (props) => {
  const svgRef = useRef(null)
  const containerRef = useRef(null)
  const tooltipRef = useRef(null)
  const [days, setDays] = useState(30)
  const [view, setView] = useState('chart')
  const [showValues, setShowValues] = useState(false)
  const [day, setDay] = useState(() => moment().startOf('day'))

  // 1 Day = one point per 15 MINUTES of a single local day (the slot's peak
  // walking minute), same buckets as Temperature. The rest of the draw is
  // unchanged: quarterHourCadence returns entries carrying a real Date, so the
  // existing time scale just gets a one-day domain and hour ticks.
  const hourly = days === 1

  useEffect(() => {
    if (view === 'chart') draw()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data, props.surgeryDate, days, view, day.valueOf(), showValues])

  const getDailyCadence = () => {
    if (!props.data || props.data.length === 0) return { dailyData: [] }

    if (hourly) {
      const { start, end } = dayBounds(day)
      // Keep every slot so the axis spans the whole day; the line and the dots
      // skip the nulls, so a quiet quarter-hour is a gap rather than a zero.
      const dailyData = quarterHourCadence(props.data, day).map((h) => ({
        date: h.date,
        slot: h.slot,
        time: h.time,
        maxCadence: h.maxCadence,
        walkingMinutes: h.walkingMinutes,
        totalSteps: h.totalSteps,
      }))
      if (!dailyData.some((d) => d.maxCadence != null)) return { dailyData: [] }
      return { dailyData, windowStart: start.toDate(), windowEnd: end.toDate() }
    }

    // Falls back to the patient's latest reading when the requested range
    // holds none — tracpatch.window.js explains why, and why widening the
    // default range is not the fix.
    const win = effectiveWindow(props.data, days, (r) => r && r.startTime)
    const cutoff = win.windowStart
    const today = win.windowEnd.clone().endOf('day')

    // Group walking minutes by day; sum steps within walking minutes only.
    const byDay = {}
    props.data.forEach((record) => {
      if (!record || !record.startTime) return
      const t = moment(record.startTime)
      if (t.isBefore(cutoff) || t.isAfter(today)) return
      if (!record.steps || record.steps <= 0) return // walking minutes only
      const day = t.format('YYYY-MM-DD')
      if (!byDay[day]) byDay[day] = { totalSteps: 0, walkingMinutes: 0, maxCadence: 0, lastTime: null }
      byDay[day].totalSteps += record.steps
      byDay[day].walkingMinutes += 1
      byDay[day].maxCadence = Math.max(byDay[day].maxCadence, record.steps)
      // Track the day's latest reading timestamp for the data-grid's date+time.
      if (!byDay[day].lastTime || t.isAfter(moment(byDay[day].lastTime))) {
        byDay[day].lastTime = record.startTime
      }
    })

    const dailyData = Object.entries(byDay)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, v]) => ({
        date: moment(date, 'YYYY-MM-DD').startOf('day').toDate(),
        time: v.lastTime,
        maxCadence: v.maxCadence,
        walkingMinutes: v.walkingMinutes,
        totalSteps: v.totalSteps,
      }))

    if (dailyData.length === 0) return { dailyData: [] }

    // Right-justify: end the window at the last day with data so there's no
    // blank tail to the right. Left edge still floored at the N-day cutoff.
    const firstData = moment(dailyData[0].date).startOf('day')
    const lastData = moment(dailyData[dailyData.length - 1].date).startOf('day')
    const windowStart = firstData.isAfter(cutoff) ? firstData.toDate() : cutoff.toDate()
    const todayStart = win.windowEnd.clone().startOf('day')
    const windowEnd = lastData.isBefore(todayStart) ? lastData.toDate() : todayStart.toDate()
    return { dailyData, windowStart, windowEnd, isStale: win.isStale }
  }

  const draw = () => {
    const { dailyData, windowStart, windowEnd } = getDailyCadence()
    if (!dailyData || dailyData.length === 0) return

    const margin = { top: 30, right: 20, bottom: 60, left: 55 }
    const screenWidth = containerRef.current ? containerRef.current.offsetWidth : 400
    const screenHeight = 300
    const width = screenWidth - margin.left - margin.right
    const height = screenHeight - margin.top - margin.bottom

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    // A redraw replaces the point under the pointer, so its mouseout never
    // fires and a tooltip left visible would float there until the next
    // hover. Every draw therefore starts with the tooltip hidden.
    d3.select(tooltipRef.current).style('visibility', 'hidden')

    const g = svg
      .attr('width', screenWidth)
      .attr('height', screenHeight)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    let xMin = windowStart
    let xMax = windowEnd
    // A single day of data gives a zero-width time domain: the point would pin
    // to an edge and d3.timeDay ticks would produce nothing (no date shown).
    // Pad by half a day each side so the point/label sit mid-chart.
    if (moment(xMin).isSame(xMax)) {
      xMin = moment(windowStart).subtract(12, 'hours').toDate()
      xMax = moment(windowEnd).add(12, 'hours').toDate()
    }
    const x = d3.scaleTime().domain([xMin, xMax]).range([0, width])

    // Expected band, one point per DAY across the window (not per data point):
    // a band drawn only where the patient happened to walk would break wherever
    // they rested, which is exactly where a clinician wants the reference.
    const bandData = []
    if (!hourly && props.surgeryDate) {
      const sd = moment(props.surgeryDate).startOf('day')
      if (sd.isValid()) {
        const cursor = moment(xMin).startOf('day')
        const last = moment(xMax).startOf('day')
        while (cursor.isSameOrBefore(last)) {
          const r = cadenceRangeForDayOffset(cursor.diff(sd, 'days'))
          if (r) bandData.push({ date: cursor.toDate(), min: r.min, max: r.max })
          cursor.add(1, 'day')
        }
      }
    }

    const yPeak = Math.max(
      d3.max(dailyData, (d) => d.maxCadence) || 0,
      d3.max(bandData, (d) => d.max) || 0,
      60
    )
    // Headroom for a future target band overlay (laid out per design doc:
    // "lay the chart so a target band can be slotted in later without
    // restructure"). Keep the line drawn on top of where a band would sit.
    const y = d3.scaleLinear().domain([0, Math.max(yPeak * 1.15, 40)]).range([height, 0])

    // Axes. With few data points (incl. a single day) label each actual day so
    // the date always shows; for denser ranges fall back to an even interval.
    const xAxis = d3.axisBottom(x).tickFormat(hourly ? (t) => compactHourLabel(moment(t).hour()) : d3.timeFormat('%m/%d'))
    const spanDays = Math.round((xMax - xMin) / 86400000) + 1
    if (hourly) {
      xAxis.ticks(d3.timeHour.every(2))
    } else if (spanDays <= 14) {
      // Tick every DAY in the window, not every day that has data. A rest day
      // is part of the recovery picture: dropping 09/26 and 09/27 off the axis
      // made a three-day gap look like two adjacent readings, and the line
      // between them look like a continuous trend.
      xAxis.ticks(d3.timeDay.every(1))
    } else if (dailyData.length <= 8) {
      xAxis.tickValues(dailyData.map((d) => d.date))
    } else {
      xAxis.ticks(d3.timeDay.every(Math.max(1, Math.floor(days / 8))))
    }
    g.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(xAxis)
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)')
      .style('fill', 'black')
      .style('stroke', 'none')
      .call(styleAxisTicks, 11)

    g.append('text')
      .attr('text-anchor', 'end')
      .attr('x', width / 2 + margin.left)
      .attr('y', height + margin.bottom - 5)
      .attr('stroke', 'none')
      .attr('fill', 'black')
      .style('font-size', AXIS_TITLE_FONT_SIZE)
      .style('font-weight', AXIS_TITLE_FONT_WEIGHT)
      .text(hourly ? 'Time' : 'Date')

    g.append('g')
      .call(d3.axisLeft(y).ticks(6).tickFormat(d3.format(',.0f')))
      .selectAll('text')
      .style('fill', 'black')
      .style('stroke', 'none')
      .call(styleAxisTicks, 10)

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -margin.left + 12)
      .attr('x', -height / 2)
      .attr('text-anchor', 'middle')
      .attr('fill', 'black')
      .attr('stroke', 'none')
      .style('font-size', AXIS_TITLE_FONT_SIZE)
      .style('font-weight', AXIS_TITLE_FONT_WEIGHT)
      .text('Max cadence (spm)')

    // Surgery-date marker — vertical dashed black line + "Surgery M/D"
    // label. Only drawn if the surgery date falls inside the visible window.
    if (!hourly && props.surgeryDate) {
      const sd = moment(props.surgeryDate)
      // 🔴 Compare DAYS, not instants. windowEnd is the last data day at
      // MIDNIGHT, so a surgery date carrying any time of day (08:00, or a UTC
      // timestamp that lands mid-morning locally) tested as "after the window"
      // and the marker silently vanished -- which is exactly what happened to
      // a 9/28 surgery on a chart whose last day was 9/28.
      const sDay = sd.clone().startOf('day')
      const inWindow =
        sd.isValid() &&
        !sDay.isBefore(moment(xMin).startOf('day')) &&
        !sDay.isAfter(moment(xMax).startOf('day'))
      if (inWindow) {
        const sx = x(sDay.toDate())
        g.append('line')
          .attr('x1', sx)
          .attr('x2', sx)
          .attr('y1', 0)
          .attr('y2', height)
          .attr('stroke', '#000')
          .attr('stroke-width', 1.5)
          .attr('stroke-dasharray', '4,4')
        const labelText = 'Surgery ' + sd.format('M/D')
        const labelOnLeft = sx > width - 80
        g.append('text')
          .attr('x', sx + (labelOnLeft ? -4 : 4))
          .attr('y', -8)
          .attr('text-anchor', labelOnLeft ? 'end' : 'start')
          .style('font-size', '11px')
          .style('font-weight', '600')
          .style('fill', '#000')
          .text(labelText)
      }
    }

    // Expected range as two dashed green lines, matching the Steps chart.
    if (bandData.length > 0) {
      ;['min', 'max'].forEach((key) => {
        g.append('path')
          .datum(bandData)
          .attr('fill', 'none')
          .attr('stroke', CHART_COLORS.completeGreen)
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '6,4')
          .attr('stroke-linejoin', 'round')
          .attr('d', d3.line().x((d) => x(d.date)).y((d) => y(d[key])))
      })
      const legend = g.append('g').attr('transform', 'translate(0, -18)')
      legend.append('line')
        .attr('x1', 0).attr('x2', 18).attr('y1', 4).attr('y2', 4)
        .attr('stroke', CHART_COLORS.completeGreen).attr('stroke-width', 3)
      legend.append('text')
        .attr('x', 24).attr('y', 8)
        .style('font-size', '11px').style('fill', '#333').style('stroke', 'none')
        .text('Expected range (min/max)')
    }

    // Cadence line — connect through rest days so the line stays
    // continuous across the chart. Dots still mark only days with data,
    // so the underlying rest pattern is visible without breaking the line.
    const lineData = dailyData.filter((d) => d.maxCadence != null)
    const lineGen = d3
      .line()
      .x((d) => x(d.date))
      .y((d) => y(d.maxCadence))
      .curve(d3.curveMonotoneX)

    g.append('path')
      .datum(lineData)
      .attr('fill', 'none')
      .attr('stroke', CHART_COLORS.lineBlue)
      .attr('stroke-width', 2)
      .attr('d', lineGen)

    const tooltip = d3.select(tooltipRef.current)

    // Points on each plotted day so single-day stints between gaps are visible.
    g.selectAll('.cadence-pt')
      .data(dailyData.filter((d) => d.maxCadence != null))
      .enter()
      .append('circle')
      .attr('class', 'cadence-pt')
      .attr('cx', (d) => x(d.date))
      .attr('cy', (d) => y(d.maxCadence))
      .attr('r', hourly ? 2.5 : 3.5)
      .attr('fill', CHART_COLORS.lineBlue)
      .style('cursor', hourly ? 'pointer' : 'zoom-in')
      .on('mouseover', function (d) {
        const whenLabel = hourly
          ? `<b>Time</b>: ${quarterRangeLabel(d.slot)}`
          : `<b>Date</b>: ${moment(d.date).format('MM/DD')}`
        tooltip
          .style('visibility', 'visible')
          .html(
            `${whenLabel}<br/>` +
              `<b>Max cadence</b>: ${Math.round(d.maxCadence)} spm<br/>` +
              `<b>Walking</b>: ${d.walkingMinutes} min<br/>` +
              `<b>Total steps</b>: ${d.totalSteps.toLocaleString()}` +
              (hourly ? '' : '<br/><i>Click for this day</i>')
          )
          .style('top', y(d.maxCadence) - 60 + 'px')
          .style('left', x(d.date) + 10 + 'px')
      })
      .on('mouseout', () => tooltip.style('visibility', 'hidden'))
      .on('click', (d) => {
        if (hourly || !d.date) return
        tooltip.style('visibility', 'hidden')
        setDay(moment(d.date).startOf('day'))
        setDays(1)
      })

    // Value labels — OFF unless the chart's "Show values" switch is on.
    if (!showValues) return
    if (bandData.length > 0) {
      ;['min', 'max'].forEach((key) => {
        g.selectAll('.cadence-range-' + key)
          .data(bandData)
          .enter()
          .append('text')
          .attr('class', 'cadence-range-' + key)
          .attr('x', (d) => x(d.date))
          .attr('y', (d) => y(d[key]) + (key === 'max' ? -6 : 12))
          .attr('text-anchor', 'middle')
          .style('font-size', '9px')
          .style('fill', CHART_COLORS.completeGreen)
          .style('font-weight', '600')
          .style('pointer-events', 'none')
          .text((d) => Math.round(d[key]))
      })
    }
    dailyData.filter((d) => d.maxCadence != null).forEach((d) => {
      g.append('text')
        .attr('x', x(d.date))
        .attr('y', y(d.maxCadence) - 5)
        .attr('text-anchor', 'middle')
        .attr('fill', CHART_COLORS.lineBlue)
        .style('font-size', dailyData.length > 20 ? '9px' : '10px')
        .style('font-weight', '600')
        .style('pointer-events', 'none')
        .text(Math.round(d.maxCadence))
    })
  }

  const { dailyData } = getDailyCadence()
  // Same inputs the chart uses, so banner and chart cannot disagree.
  const stale = effectiveWindow(props.data, days, (r) => r && r.startTime)
  const gridRows = dailyData
    .filter((d) => d.maxCadence != null)
    .map((d) => ({ date: d.time || d.date, value: Math.round(d.maxCadence) + ' spm' }))
  const firstDay = earliestDay(props.data, 'startTime')

  return (
    <>
      <center>
        <h4>TracPatch Cadence</h4>
      </center>
      {stale.isStale && (
        <Row justify='start' style={{ marginBottom: 8, paddingLeft: 12 }}>
          <div style={{ background:'#fff8e1', border:'1px solid var(--orange-color, #f5881f)',
                        borderRadius:8, padding:'6px 12px', fontSize:12, color:'#8a4b00', maxWidth:'100%' }}>
            {staleRangeLabel(stale.windowStart, stale.windowEnd, days)}
          </div>
        </Row>
      )}
      <Row justify='start' className='chartControls' style={{ marginBottom: 8, paddingLeft: 12 }}>
        <Button.Group size='small'>
          <Button type={days === 1 ? 'primary' : 'default'} onClick={() => setDays(1)}>1 Day</Button>
          <Button type={days === 7 ? 'primary' : 'default'} onClick={() => setDays(7)}>7 Days</Button>
          <Button type={days === 14 ? 'primary' : 'default'} onClick={() => setDays(14)}>14 Days</Button>
          <Button type={days === 30 ? 'primary' : 'default'} onClick={() => setDays(30)}>30 Days</Button>
        </Button.Group>
        <ShowValuesSwitch id='cadence-show-values' checked={showValues} onChange={setShowValues} />
        <DataViewToggle
          active={view === 'data'}
          onClick={() => setView(view === 'data' ? 'chart' : 'data')}
        />
      </Row>
      {hourly && (
        <Row justify='center' align='middle' className='chartDayNav' style={{ marginBottom: 8, gap: 8 }}>
          <Button size='small' aria-label='Previous day' icon={<CaretLeftOutlined />}
            disabled={!firstDay || !day.isAfter(firstDay)} onClick={() => setDay(day.clone().subtract(1, 'day').startOf('day'))} />
          <span style={{ minWidth: 150, textAlign: 'center', fontWeight: 600 }}>{day.format('ddd, MMM D, YYYY')}</span>
          <Button size='small' aria-label='Next day' icon={<CaretRightOutlined />}
            disabled={isTodayOrLater(day)} onClick={() => setDay(day.clone().add(1, 'day').startOf('day'))} />
        </Row>
      )}
      {dailyData.length > 0 ? (
        view === 'data' ? (
          <ChartDataGrid rows={gridRows} valueLabel='Max cadence (spm)' csvName='tracpatch-cadence' />
        ) : (
          <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
            <div
              ref={tooltipRef}
              className='chartToolTip'
              style={{ position: 'absolute', visibility: 'hidden', zIndex: 10 }}
            />
            <svg style={{ width: '100%', height: '300px' }} ref={svgRef} />
          </div>
        )
      ) : (
        <Row justify='center' align='middle' style={{ height: 200 }}>
          <h2>{hourly ? 'No walking recorded for this day' : 'Data not available'}</h2>
        </Row>
      )}
    </>
  )
}

const mapStateToProps = (state) => ({ patient: state.patient.patient })
const mapDispatchToProps = () => ({})
export default connect(mapStateToProps, mapDispatchToProps)(TracPatchCadenceChart)
